import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PatientDemographicsTab from "./components/PatientDemographicsTab";
import PatientAssessmentTab from "./components/PatientAssessmentTab";
import PatientDocumentsTab from "./components/PatientDocumentsTab";
import PatientMedicationTab from "./components/PatientMedicationTab";
import PatientAuthScheduleTab from "./components/PatientAuthScheduleTab";
import PatientEvalOrdersTab from "./components/PatientEvalOrdersTab";
import PatientStaffTab from "./components/PatientStaffTab";
import PatientSummaryPanel from "./components/PatientSummaryPanel";
import PatientSearchToolbar from "./components/PatientSearchToolbar";
import { getPatientById, getPatientEpisode, getPatientCharts } from "../../services/patientService";
import { ChevronRightIcon } from "../../Icons";
import {
  canManageDemographics,
  canManageDocuments,
  canAssignStaff,
  canEditDocsNotes,
  canDeleteClinicalForms,
} from "./patientPermissions";
import {
  PATIENT_DETAIL_VIEWPORT,
  PATIENT_DETAIL_SPLIT,
  PATIENT_DETAIL_SIDEBAR,
  PATIENT_DETAIL_MAIN,
  PATIENT_DETAIL_MAIN_SCROLL,
  PATIENT_PAGE_SHELL,
} from "./patientPageLayout";
import { displayDateUS } from "../../utils/dateUtils";
import { resolveLatestEpisodePath } from "./resolveLatestEpisodePath";
import { patientWithChartContext } from "./patientWithChartContext";
import { PATIENT_THEME, patientThemeCssVars } from "./patientTheme";
import { MODULE_TAB_BTN, MODULE_TAB_ICON_SIZE } from "../../ui/pageChrome";
import {
  filterVisibleTabs,
  isPatientTabKey,
  pickDefaultTab,
  tabNavGridClass,
} from "./patientTabs";
import { patchSearchParams } from "../../hooks/useUrlTab";
import usePatientTabs from "./usePatientTabs";

const PatientDetail = () => {
  const { id, chartId, episodeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    enabledTabKeys,
    isTabEnabled,
    defaultTab,
    loading: tabsLoading,
  } = usePatientTabs();

  const visibleTabs = filterVisibleTabs(enabledTabKeys);
  const initialTab = pickDefaultTab(enabledTabKeys) || "demographics";
  const urlTab = searchParams.get("tab");
  const urlSection = searchParams.get("section");

  const [activeTab, setActiveTab] = useState(() =>
    isPatientTabKey(urlTab) ? urlTab : initialTab,
  );
  const [evalFocusSection, setEvalFocusSection] = useState(
    urlTab === "eval" ? urlSection : null,
  );
  const [evalFocusNonce, setEvalFocusNonce] = useState(0);
  const [data, setData] = useState(null);
  const [episodeMeta, setEpisodeMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const backTarget = useRef({
    path: location.state?.returnTo || "/patients",
    label: location.state?.backLabel || "Back to patients",
  });

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = currentUser?.role?.name;
  const canEditDemographics = canManageDemographics(role);
  const canManageDocs = canManageDocuments(role);
  const canEditContent = canEditDocsNotes(role);
  const canAssign = canAssignStaff(role);
  const canDeleteForms = canDeleteClinicalForms(role);

  const loadPatient = useCallback(
    async (opts = {}) => {
      const silent = Boolean(opts.silent);
      if (!silent) setLoading(true);
      try {
        if (!chartId || !episodeId) {
          navigate(`/patients/${id}/charts`, { replace: true });
          return;
        }
        const [response, episodeRes] = await Promise.all([
          getPatientById(id, chartId ? { chartId } : {}),
          getPatientEpisode(id, chartId, episodeId),
        ]);
        setData(response.result || response.data || response);
        setEpisodeMeta(
          episodeRes?.result?.episode || episodeRes?.episode || null,
        );
      } catch (err) {
        toast.error(err.message || "Failed to load patient");
        if (!silent) navigate(`/patients/${id}/charts`);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, chartId, episodeId, navigate],
  );

  const persistPatientTab = useCallback(
    (tabId, section = null) => {
      setSearchParams(
        (prev) =>
          patchSearchParams(prev, {
            tab: tabId || null,
            section: tabId === "eval" && section ? section : null,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const goToPatientTab = useCallback(
    (tabId, section = null) => {
      setActiveTab(tabId);
      const nextSection = tabId === "eval" ? section : null;
      setEvalFocusSection(nextSection);
      if (tabId === "eval" && section) {
        setEvalFocusNonce((n) => n + 1);
      }
      persistPatientTab(tabId, nextSection);
    },
    [persistPatientTab],
  );

  useEffect(() => {
    loadPatient();
  }, [loadPatient]);

  // Keep active tab valid when tenant config loads / changes.
  useEffect(() => {
    if (tabsLoading) return;
    if (!defaultTab) return;
    if (!isTabEnabled(activeTab)) {
      goToPatientTab(defaultTab);
    }
  }, [tabsLoading, defaultTab, activeTab, isTabEnabled, goToPatientTab]);

  // Apply ?tab= on load/refresh, and consume location.state tab once.
  useEffect(() => {
    if (tabsLoading) return;

    const stateTab = location.state?.tab;
    const evalSection = location.state?.evalSection || null;
    if (stateTab && isPatientTabKey(stateTab)) {
      if (!isTabEnabled(stateTab)) {
        toast.error("That patient tab is not enabled for this agency");
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: null },
        );
        return;
      }
      goToPatientTab(stateTab, evalSection);
      navigate(
        {
          pathname: location.pathname,
          search: patchSearchParams(searchParams, {
            tab: stateTab,
            section: stateTab === "eval" && evalSection ? evalSection : null,
          }).toString(),
        },
        { replace: true, state: null },
      );
      return;
    }

    const qTab = searchParams.get("tab");
    const qSection = searchParams.get("section");
    if (qTab && isPatientTabKey(qTab) && isTabEnabled(qTab)) {
      setActiveTab(qTab);
      setEvalFocusSection(qTab === "eval" ? qSection : null);
    } else if (isPatientTabKey(activeTab) && isTabEnabled(activeTab)) {
      persistPatientTab(
        activeTab,
        activeTab === "eval" ? evalFocusSection : null,
      );
    }
  }, [
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigate,
    tabsLoading,
    isTabEnabled,
    goToPatientTab,
    searchParams,
    persistPatientTab,
    activeTab,
    evalFocusSection,
  ]);

  const openPatientLatestEpisode = useCallback(
    async (patientId) => {
      if (!patientId) return;
      try {
        const chartsRes = await getPatientCharts(patientId);
        const payload = chartsRes.result || chartsRes;
        navigate(resolveLatestEpisodePath(patientId, payload));
      } catch (err) {
        toast.error(err.message || "Failed to open patient");
        navigate(`/patients/${patientId}/charts`);
      }
    },
    [navigate],
  );

  if (loading || tabsLoading) {
    return (
      <div className={`${PATIENT_PAGE_SHELL} animate-pulse`}>
        <div className="h-11 bg-slate-100 border border-slate-200 rounded-lg" />
        <div className="h-72 bg-slate-100 border border-slate-200 rounded-lg" />
      </div>
    );
  }

  if (!data?.patient) return null;

  const { patient: rawPatient, assignedStaff, summary } = data;
  const patient = patientWithChartContext(rawPatient, episodeMeta);
  const chartScopeKey = `chart-${chartId}-ep-${episodeId}`;

  const episodeRangeLabel = episodeMeta
    ? `Episode #${episodeMeta.episodeNumber} ( ${displayDateUS(episodeMeta.startDate) || "—"} - ${displayDateUS(episodeMeta.endDate) || "—"} )`
    : null;

  return (
    <div
      className={`${PATIENT_DETAIL_VIEWPORT} patient-module`}
      style={patientThemeCssVars()}
    >
      <div className="flex flex-wrap items-center gap-2 min-w-0 shrink-0">
        <button
          type="button"
          onClick={() => navigate(`/patients/${id}/charts`)}
          className={`text-[11px] font-medium ${PATIENT_THEME.button.link}`}
        >
          Charts
        </button>
        {episodeMeta?.chart ? (
          <>
            <ChevronRightIcon size={13} className="text-slate-300 shrink-0" />
            <span className="text-[11px] font-semibold text-slate-700">
              Chart #{episodeMeta.chart.chartNumber}
            </span>
            <ChevronRightIcon size={13} className="text-slate-300 shrink-0" />
            <span className="text-[11px] font-semibold text-slate-700 truncate min-w-0">
              {episodeRangeLabel}
            </span>
          </>
        ) : null}
      </div>

      <div className="shrink-0">
        <PatientSearchToolbar
          patient={patient}
          onSelectPatient={openPatientLatestEpisode}
          onClose={() => navigate(backTarget.current.path)}
          closeTitle={backTarget.current.label}
        />
      </div>

      <div className={PATIENT_DETAIL_SPLIT}>
        <aside className={PATIENT_DETAIL_SIDEBAR} aria-label="Patient summary">
          <PatientSummaryPanel
            summary={summary}
            patient={patient}
            assignedStaff={assignedStaff || []}
          />
        </aside>

        <div className={PATIENT_DETAIL_MAIN}>
          {visibleTabs.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8 text-sm text-slate-500">
              No patient tabs are enabled for this agency. Ask an administrator
              to enable tabs in tenant configuration.
            </div>
          ) : (
            <>
              <nav className={tabNavGridClass(visibleTabs.length)}>
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => goToPatientTab(tab.id)}
                      className={`${MODULE_TAB_BTN} border-slate-200 ${
                        active
                          ? PATIENT_THEME.tab.active
                          : PATIENT_THEME.tab.inactive
                      }`}
                    >
                      <Icon size={MODULE_TAB_ICON_SIZE} className="shrink-0" />
                      <span className="truncate">{tab.label}</span>
                      {active && (
                        <span
                          className={`absolute left-0 right-0 bottom-0 h-0.5 ${PATIENT_THEME.tab.underline}`}
                        />
                      )}
                    </button>
                  );
                })}
              </nav>

              <div className={PATIENT_DETAIL_MAIN_SCROLL} key={chartScopeKey}>
                {activeTab === "demographics" && isTabEnabled("demographics") && (
                  <PatientDemographicsTab
                    patient={patient}
                    chartId={chartId}
                    canEdit={canEditDemographics}
                    canEditNotes={canEditContent}
                    onSaved={loadPatient}
                  />
                )}
                {activeTab === "assessment" && isTabEnabled("assessment") && (
                  <PatientAssessmentTab
                    patientId={id}
                    chartId={chartId}
                    patient={patient}
                    canEdit={canEditDemographics}
                    canEditNotes={canEditContent}
                  />
                )}
                {activeTab === "documents" && isTabEnabled("documents") && (
                  <PatientDocumentsTab
                    patientId={id}
                    chartId={chartId}
                    episodeId={episodeId}
                    patient={patient}
                    canManage={canManageDocs}
                    onRefresh={loadPatient}
                  />
                )}
                {activeTab === "medication" && isTabEnabled("medication") && (
                  <PatientMedicationTab
                    patientId={id}
                    patient={patient}
                    chartId={chartId}
                    canEdit={canEditContent}
                    onRefresh={loadPatient}
                  />
                )}
                {activeTab === "auth" && isTabEnabled("auth") && (
                  <PatientAuthScheduleTab
                    patient={patient}
                    chartId={chartId}
                    canEdit={canEditContent}
                    canDelete={canDeleteForms}
                    onSaved={loadPatient}
                    episodeMeta={episodeMeta}
                    evalTabEnabled={isTabEnabled("eval")}
                  />
                )}
                {activeTab === "eval" && isTabEnabled("eval") && (
                  <PatientEvalOrdersTab
                    patientId={id}
                    patient={patient}
                    chartId={chartId}
                    episodeId={episodeId}
                    canEdit={canEditContent}
                    canDelete={canDeleteForms}
                    showSelection={canDeleteForms}
                    focusSectionKey={evalFocusSection}
                    focusNonce={evalFocusNonce}
                  />
                )}
                {activeTab === "staff" && isTabEnabled("staff") && (
                  <PatientStaffTab
                    patientId={id}
                    chartId={chartId}
                    assignedStaff={assignedStaff || []}
                    canAssign={canAssign}
                    onRefresh={() => loadPatient({ silent: true })}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientDetail;
